/**
 * UbiquitousLanguage - The shared vocabulary within a Bounded Context.
 *
 * In Domain-Driven Design, the Ubiquitous Language is the shared terminology
 * used by all team members within a Bounded Context. It bridges the gap between
 * domain experts and developers.
 *
 * This implementation represents the Ubiquitous Language as a collection of
 * SKOS vocabularies:
 * - Imported Domain Vocabularies: SKOS vocabularies from domain ontologies
 * - Local Vocabularies: SKOS vocabularies defined within this context
 *
 * The Ubiquitous Language provides the Labeling Context for diagrams,
 * ensuring consistent terminology when visualizing domain concepts.
 */

import type Vocabulary from "@semantical/modules/vocabulary/Vocabulary";

/**
 * Reference to a vocabulary (imported or local).
 */
export interface VocabularyRef {
    /** URI identifier for the vocabulary */
    readonly uri: string;

    /** Display label for the vocabulary */
    readonly label: string;

    /** Whether this vocabulary is imported from domain or locally defined */
    readonly source: 'imported' | 'local';

    /** Optional path to the TTL file (for local vocabularies) */
    readonly ttlPath?: string;

    /** Optional URL to download the full vocabulary (for imported vocabularies) */
    readonly downloadUrl?: string;

    /** The actual Vocabulary instance (loaded lazily) */
    vocabulary?: Vocabulary;
}

/**
 * Ubiquitous Language - Collection of SKOS vocabularies for a BoundedContext.
 *
 * The Ubiquitous Language provides:
 * 1. Shared terminology for the bounded context
 * 2. Labeling context options for diagrams
 * 3. Consistent vocabulary for domain concepts
 */
export interface UbiquitousLanguage {
    /** Unique identifier */
    readonly id: string;

    /** Display name */
    readonly name: string;

    /** Vocabularies imported from domain ontologies */
    getImportedVocabularies(): VocabularyRef[];

    /** Locally defined vocabularies within this context */
    getLocalVocabularies(): VocabularyRef[];

    /** Get all vocabularies (imported + local) */
    getAllVocabularies(): VocabularyRef[];

    /** Add an imported vocabulary reference */
    addImportedVocabulary(ref: VocabularyRef): void;

    /** Add a local vocabulary reference */
    addLocalVocabulary(ref: VocabularyRef): void;

    /** Remove a vocabulary by URI */
    removeVocabulary(uri: string): boolean;

    /** Get a vocabulary by URI */
    getVocabularyByUri(uri: string): VocabularyRef | undefined;

    /** Check if a vocabulary exists */
    hasVocabulary(uri: string): boolean;
}

/**
 * Default implementation of UbiquitousLanguage.
 */
export class UbiquitousLanguageImpl implements UbiquitousLanguage {
    readonly id: string;
    readonly name: string;

    private _importedVocabularies: VocabularyRef[] = [];
    private _localVocabularies: VocabularyRef[] = [];

    constructor(params: { id: string; name: string }) {
        this.id = params.id;
        this.name = params.name;
    }

    getImportedVocabularies(): VocabularyRef[] {
        return [...this._importedVocabularies];
    }

    getLocalVocabularies(): VocabularyRef[] {
        return [...this._localVocabularies];
    }

    getAllVocabularies(): VocabularyRef[] {
        return [...this._importedVocabularies, ...this._localVocabularies];
    }

    addImportedVocabulary(ref: VocabularyRef): void {
        if (!this._importedVocabularies.find(v => v.uri === ref.uri)) {
            this._importedVocabularies.push({
                ...ref,
                source: 'imported'
            });
        }
    }

    addLocalVocabulary(ref: VocabularyRef): void {
        if (!this._localVocabularies.find(v => v.uri === ref.uri)) {
            this._localVocabularies.push({
                ...ref,
                source: 'local'
            });
        }
    }

    removeVocabulary(uri: string): boolean {
        const importedIndex = this._importedVocabularies.findIndex(v => v.uri === uri);
        if (importedIndex >= 0) {
            this._importedVocabularies.splice(importedIndex, 1);
            return true;
        }

        const localIndex = this._localVocabularies.findIndex(v => v.uri === uri);
        if (localIndex >= 0) {
            this._localVocabularies.splice(localIndex, 1);
            return true;
        }

        return false;
    }

    getVocabularyByUri(uri: string): VocabularyRef | undefined {
        return this.getAllVocabularies().find(v => v.uri === uri);
    }

    hasVocabulary(uri: string): boolean {
        return this.getVocabularyByUri(uri) !== undefined;
    }

    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON(): {
        '@context': Record<string, unknown>;
        '@type': 'UbiquitousLanguage';
        includesVocabulary: Array<{
            '@type': 'Vocabulary';
            '@id': string;
            label: string;
            source: 'imported' | 'local';
            ttlPath?: string;
            downloadUrl?: string;
        }>;
    } {
        // Merge imported and local vocabularies with source property
        const allVocabularies = [
            ...this._importedVocabularies.map(v => ({
                '@type': 'Vocabulary' as const,
                '@id': v.uri,
                label: v.label,
                source: 'imported' as const,
                ...(v.ttlPath ? { ttlPath: v.ttlPath } : {}),
                ...(v.downloadUrl ? { downloadUrl: v.downloadUrl } : {})
            })),
            ...this._localVocabularies.map(v => ({
                '@type': 'Vocabulary' as const,
                '@id': v.uri,
                label: v.label,
                source: 'local' as const,
                ...(v.ttlPath ? { ttlPath: v.ttlPath } : {}),
                ...(v.downloadUrl ? { downloadUrl: v.downloadUrl } : {})
            }))
        ];

        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                includesVocabulary: { '@type': '@id', '@container': '@set' }
            },
            '@type': 'UbiquitousLanguage',
            includesVocabulary: allVocabularies
        };
    }

    /**
     * Deserialize from JSON-LD or legacy format.
     */
    static fromJSON(json: {
        // Legacy format
        id?: string;
        name?: string;
        importedVocabularies?: Array<{ uri: string; label: string; source?: string; ttlPath?: string; downloadUrl?: string }>;
        localVocabularies?: Array<{ uri: string; label: string; source?: string; ttlPath?: string; downloadUrl?: string }>;
        // JSON-LD format
        '@type'?: 'UbiquitousLanguage';
        includesVocabulary?: Array<{ '@id': string; label: string; source: 'imported' | 'local'; ttlPath?: string; downloadUrl?: string }>;
    }): UbiquitousLanguageImpl {
        const ul = new UbiquitousLanguageImpl({
            id: json.id ?? 'ubiquitous-language',
            name: json.name ?? 'Ubiquitous Language'
        });

        // Handle JSON-LD format (includesVocabulary)
        if (json.includesVocabulary) {
            for (const v of json.includesVocabulary) {
                const ref: VocabularyRef = {
                    uri: v['@id'],
                    label: v.label,
                    source: v.source,
                    ttlPath: v.ttlPath,
                    downloadUrl: v.downloadUrl
                };
                if (v.source === 'imported') {
                    ul.addImportedVocabulary(ref);
                } else {
                    ul.addLocalVocabulary(ref);
                }
            }
        }

        // Handle legacy format (importedVocabularies/localVocabularies)
        if (json.importedVocabularies) {
            for (const v of json.importedVocabularies) {
                ul.addImportedVocabulary({
                    uri: v.uri,
                    label: v.label,
                    source: 'imported',
                    ttlPath: v.ttlPath,
                    downloadUrl: v.downloadUrl
                });
            }
        }

        if (json.localVocabularies) {
            for (const v of json.localVocabularies) {
                ul.addLocalVocabulary({
                    uri: v.uri,
                    label: v.label,
                    source: 'local',
                    ttlPath: v.ttlPath,
                    downloadUrl: v.downloadUrl
                });
            }
        }

        return ul;
    }
}
